import './select-item-multiple-form.scss'

import { debounce } from 'lodash'
import { defaultIfEmpty, first, map, pluck, switchMap, tap } from 'rxjs/operators'
import { Exception } from '@zxing/library'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { faUndo, faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { SearchResult, Item, Movement, SearchFeatured, Wemi, SearchQuery } from '@skinsoft/types'
import { wemiSelectors } from '@skinsoft/selectors'
import { ItemDataStore, MovementDataStore } from '@skinsoft/entity'
import { searchResultSelectors } from '@skinsoft/search-data-store'
import { isWemi } from '@skinsoft/wemi-utils'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { BarcodeReader, BarcodeSubscriber, Form } from '@skinsoft/ui'
import { BasicItemCard } from '@skinsoft/ui2'
import { LinkedMovementLine } from '../linked-movement-line'
import { LINK_MOVEMENT_PAGE_NAVIGATE, SELECT_ITEM_MULTIPLE_FORM_ITEM_SELECTED } from '../../../../events'

declare const config
const ACCESSORY_SEARCH_TYPES: string[] = config.accessory_search_types
const CONTAINER_SEARCH_TYPES: string[] = config.container_search_types
const ITEM_SEARCH_TYPES: string[] = config.item_search_types

const PAGE_SIZE = 5
@Component({
    components: {
        sBarcodeReader: BarcodeReader,
        sBarcodeSubscriber: BarcodeSubscriber,
        sBasicItemCard: BasicItemCard,
        sLinkedMovementLine: LinkedMovementLine,
    },
    props: {
        types: Array,
        excludedIds: {
            type: Array,
            default: () => [],
        },
        barcodeReaderEnabled: {
            type: Boolean,
            default: true
        },
        validationButtonText: {
            type: String,
            default: 'entity.addItem.form.submit'
        },
        validatingText: {
            type: String,
            default: 'entity.addItem.form.submitting'
        },
        disableConfirmation: {
            type: Boolean,
            default: false
        },
        showLinksToMovements: {
            type: Boolean,
            default: false
        },
        excludedItemsWarningMessageKey: {
            type: String,
            default: 'selectItemMultipleForm.errors.resultsAmongExcludedIds'
        },
        notifySelection: {
            type: Boolean,
            default: false
        }
    },
    template: require('./select-item-multiple-form.html'),
})
export class SelectItemMultipleForm extends ReactiveVue {
    public undoIcon = faUndo
    public loadingIcon = faCircleNotch
    public items: (Wemi | SearchResult)[] = [] as any
    public submitting = false
    public searchPending = false
    public debounce = debounce
    public item: Item = null as any
    public fullItemCardTypes = ['PhysicalMuseumItem', 'NumericMuseumItem']

    public $itemDataStore: ItemDataStore
    private $movementDataStore: MovementDataStore

    public types: string[] = [
        ...ACCESSORY_SEARCH_TYPES,
        ...CONTAINER_SEARCH_TYPES,
        ...ITEM_SEARCH_TYPES,
    ]
    public excludedIds: string[]
    public barcodeReaderEnabled: boolean
    public validationButtonText: string
    public validatingText: string
    public disableConfirmation: boolean
    private showLinksToMovements: boolean
    private excludedItemsWarningMessageKey: string
    private notifySelection: boolean
    
    private movementsContaingItems: { [itemId: string]: Movement[] } = {}

    public errorMessage: string = ''
    public currentPage: number = 0
    public searchContent: string = ''
    public nbPages: number = 0

    private readonly INPUT_FILTER_IDENTIFIER: string = 'select-item-map-form-query'
    private LINK_MOVEMENT_PAGE_NAVIGATE = LINK_MOVEMENT_PAGE_NAVIGATE

    public $refs: {
        barcodeReader: BarcodeReader
        form: Form
    }

    public async validate(values) {
        // need to be a promise to avoid conflict with $t mixin
        const errors = {} as any
        const errorMessage = this.$t('entity.addItem.form.id.notFoundError') as string

        if (!values.id) {
            this.errorMessage = this.$t('entity.addItem.form.id.emptyError') as string
        }

        if (values.id) {
            this.searchContent = values.id
            await this.performSearch(this.searchContent, 0, errorMessage)
        }
        return errors
    }

    private async performSearch(
        id: any,
        page: number,
        errorMessage: string = this.$t('entity.addItem.form.id.emptyError') as string,
    ) {
        try {
            this.searchPending = true

            try {
                await this.tryFetchingItemById(id)
            } catch (e) {
                await this.tryIdentifierSearch(id, page)
            } finally {
                if (this.items.length === 1) {
                    this.select(this.items[0] as any)
                }

                const form = this.$refs.form.form
                this.searchPending = false

                //@ts-ignore
                form.getState().values.itemId = this.items[0]
            }
        } catch (error) {
            this.searchPending = false
            this.errorMessage = errorMessage
            this.items = null as any
        }
    }

    public onBarcodeReaderInput(input: string) {
        const form = this.$refs.form.form

        form.batch(() => {
            const idState = form.getFieldState('id')
            const fieldName = 'id'
            form.change(fieldName, input)
            form.mutators.setFieldTouched(fieldName, true)
        })
    }

    public resetSearch() {
        this.$refs.form.form.change('id', '')
        this.$refs.form.form.mutators.setFieldTouched('id', true)
        this.submitting = false
        if(this.barcodeReaderEnabled) {
            this.searchContent = ''
            this.searchPending = false
            this.items = [] 
        } else {
            this.performSearch('', 0)
        }
    }

    public next() {
        this.currentPage += 1
        this.performSearch(this.searchContent, this.currentPage)
    }

    public previous() {
        this.currentPage -= 1
        this.performSearch(this.searchContent, this.currentPage)
    }

    private onPageNumberEnterred(pageNumber: string) {
        const number = parseInt(pageNumber)
        if(!isNaN(number) && number > 0 && number <= this.nbPages) {
            this.currentPage = number - 1
            this.performSearch(this.searchContent, this.currentPage)
        }
    }

    private get uiPageNumber(): number {
        return this.currentPage + 1
    }

    public get isLastPage() {
        return (this.currentPage + 1) >= this.nbPages
    }

    public select(item: Item) {
        this.item = item
        if(this.notifySelection) {
            this.$emit(SELECT_ITEM_MULTIPLE_FORM_ITEM_SELECTED, this.item)
        }
    }

    private validateSelection() {
        this.$emit('item-selected', this.item)
        this.submitting = true
    }

    private get isConfirmationDisabled() {
        return !this.items || this.items.length === 0 || this.submitting || this.disableConfirmation
    }

    private getLinkedMovements(item: SearchFeatured | Wemi | SearchResult) {
        // @ts-ignore
        return this.movementsContaingItems[item.id ?? item.itemId ?? item.item?.id] ?? []
    }

    private async tryFetchingItemById(id: string) {
        const mongoIdentifierChecker = /^[0-9a-fA-F]{24}$/
        const itemFromId =
            mongoIdentifierChecker.test(id) &&
            !this.excludedIds.includes(id) &&
            (await this.$itemDataStore
                .fetchOne(id)
                .pipe(
                    /*switchMap((item) =>
                        toSearchFeatured$(
                            { item, manifestation: null, expression: null, work: null },
                            this.getAppLocale(),
                        ),
                    ),*/
                    defaultIfEmpty(undefined as any),
                )
                .toPromise())


        if (itemFromId) {
            this.items = [itemFromId]
            this.nbPages = 1
        } else {
            throw new Exception('not an existing item id')
        }
    }

    private async tryIdentifierSearch(text: string, page: number) {
        const searchQuery: SearchQuery = {
            pageSize: PAGE_SIZE,
            types: this.types,
            page: page,
            language: this.getAppLocale(),
            queryText: text,
        }

        const result = await this.$itemDataStore
            .searchAllOnline(searchQuery, undefined, undefined, undefined, undefined, true)
            .pipe(
                first(),
                map(
                    async (searchResponse) => {
                        const items = searchResponse.allData
                        let excludedItemsThatMatched = [] as (SearchResult | Wemi)[] // used to inform user that the item is already in the movement/container

                        const filteredItems = items.filter(result => {
                                if((result as any).id) {
                                    if(this.excludedIds.includes((result as any).id)) {
                                        excludedItemsThatMatched.push(result)
                                    } else {
                                        return true
                                    }
                                } else if((result as any).item?.id) {
                                    if(this.excludedIds.includes((result as any).item.id)) {
                                        excludedItemsThatMatched.push(result)
                                    } else {
                                        return true
                                    }
                                }
                
                                return false
                        })

                        let nbPages = 0
                        try{
                            if(searchResponse.totalSize % PAGE_SIZE === 0) {
                                nbPages = (searchResponse.totalSize / PAGE_SIZE)
                            } else {
                                nbPages = Math.trunc((searchResponse.totalSize / PAGE_SIZE) + 1) 
                            }
                        } catch(err) {

                        }

                        let result = {
                            length: filteredItems.length,
                            excludedItemsThatMatched,
                            data: [] as (Wemi | SearchResult)[],
                            nbPages: nbPages
                        }

                        result.data = await Promise.all(
                            filteredItems.map(async (item) => {
                                let itemType = isWemi(item) ? wemiSelectors.itemType(item as Wemi) : searchResultSelectors.itemType(item as SearchResult) ?? ''
                                /*if(this.fullItemCardTypes.includes(itemType)) {
                                    return item
                                } else {
                                    return await toSearchFeatured$(
                                        item,
                                        this.getAppLocale(),
                                    ).toPromise()
                                }*/
                                return item
                            }),
                        )

                        return result
                    }
                ),
            )
            .toPromise()

        this.items = result.data
        this.items.splice(this.items.length) // has no effect besides triggering reactivity (component refresh) 
        this.nbPages = result.nbPages
        this.currentPage = page

        if(result.excludedItemsThatMatched && result.excludedItemsThatMatched.length) {
            this.errorMessage = this.$t(this.excludedItemsWarningMessageKey) as string
        }
    }

    @Watch('items')
    private onItemsChanged() {
        if(this.showLinksToMovements && this.items) {
            // @ts-ignore
            const itemsIds = this.items.map(item => item.id ?? item.itemId ?? item.item?.id)
            this.$movementDataStore.getMovementContainingItems(...itemsIds).subscribe(movements => {
                this.movementsContaingItems = {}
                for(const movement of movements) { // currently getMovementContainingItems() returns an array with no doubles, if that changes we should use _.uniq(movements) in the loop
                    const itemsInMovement = movement.workflowSchema.linkableItems?.filter(
                            linkableItem => linkableItem.linkableItem?.linkableItem
                        )
                        .map(
                            linkableItem => linkableItem.linkableItem!.linkableItem as string
                        )
                        .filter(itemId => itemsIds.includes(itemId))

                    if(itemsInMovement) {
                        for(const itemId of itemsInMovement) {
                            if(this.movementsContaingItems[itemId]) {
                                this.movementsContaingItems[itemId].push(movement)
                            } else {
                                this.movementsContaingItems[itemId] = [movement]
                            }
                        }
                    }
                }
            })
        }
    }
}
